import { useEffect } from 'react';
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap';

const CustomPagination = ({
  currentPage,
  pageCount,
  onPageChange,
  maxPagesToShow = 10,
}: {
  currentPage: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  maxPagesToShow?: number;
}) => {
  // If the current page falls out of range (e.g. the result set shrank after a
  // filter change), snap back to the first page. The visible window itself is
  // derived purely from currentPage below — there is no internal window state,
  // so it always contains and highlights the active page and cannot drift or
  // reset across remounts (the previous middlePage state could get stuck at 1).
  useEffect(() => {
    if (currentPage > pageCount && pageCount > 0) {
      onPageChange(1);
    }
  }, [pageCount, currentPage, onPageChange]);

  const getPageNumbers = () => {
    let startPage: number;
    let endPage: number;

    if (pageCount <= maxPagesToShow) {
      startPage = 1;
      endPage = pageCount;
    } else {
      const maxPagesBeforeCurrentPage = Math.floor(maxPagesToShow / 2);
      const maxPagesAfterCurrentPage = Math.ceil(maxPagesToShow / 2) - 1;

      if (currentPage <= maxPagesBeforeCurrentPage) {
        startPage = 1;
        endPage = maxPagesToShow;
      } else if (currentPage + maxPagesAfterCurrentPage >= pageCount) {
        startPage = pageCount - maxPagesToShow + 1;
        endPage = pageCount;
      } else {
        startPage = currentPage - maxPagesBeforeCurrentPage;
        endPage = currentPage + maxPagesAfterCurrentPage;
      }
    }

    return Array.from(
      { length: endPage + 1 - startPage },
      (_, i) => startPage + i,
    );
  };

  return (
    <Pagination aria-label="Page navigation">
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink first onClick={() => onPageChange(1)} />
      </PaginationItem>
      <PaginationItem disabled={currentPage === 1}>
        <PaginationLink
          previous
          onClick={() => onPageChange(currentPage - 1)}
        />
      </PaginationItem>

      {getPageNumbers().map((page) => (
        <PaginationItem active={page === currentPage} key={page}>
          <PaginationLink onClick={() => onPageChange(page)}>
            {page}
          </PaginationLink>
        </PaginationItem>
      ))}

      <PaginationItem disabled={currentPage === pageCount}>
        <PaginationLink next onClick={() => onPageChange(currentPage + 1)} />
      </PaginationItem>
      <PaginationItem disabled={currentPage === pageCount}>
        <PaginationLink last onClick={() => onPageChange(pageCount)} />
      </PaginationItem>
    </Pagination>
  );
};

export default CustomPagination;
